/**
 * Scenario templates — static, code-defined starting points for creating a
 * scenario. Mirrors the outbound agent template pattern (see agent-templates.ts):
 * templates are NOT persisted entities, they only pre-fill the "Create scenario"
 * form. On submit a normal Scenario row is created and owns its own config.
 */
import { OfficeHoursMode } from './workflow-dtos.js';
/**
 * Static scenario templates. Keep these generic — property/group conditions are
 * company-specific and are left for the user to fill in after seeding.
 */
export const SCENARIO_TEMPLATES = [
    {
        id: 'new-business-inbound',
        name: 'New Business',
        description: "Prospective owners evaluating or engaging the company's property management services",
        defaultSettings: {
            type: null,
            name: 'New Business',
            selectionCriteria: `## Context

New business inquiries refer to anyone reaching out to learn about, evaluate, or potentially engage the company's property management services. This includes prospective owners or decision-makers asking about services offered, pricing/fee structure, onboarding process, service coverage area, switching from another property manager, someone looking for a property manager, or general questions intended to assess whether the company is a good fit. It also includes requests for consultations, proposals, demos, or next steps related to becoming a client. Callers may not state their intent precisely — a vague "I need someone to manage my rental" or "I heard you handle properties" is still a new business inquiry.

### How to infer intent when the caller is vague

Don't wait for a perfectly stated request. Anchor on two things — the caller's role and the direction of the action — and reason from there:
- Identify whether the caller is acting as an owner/decision-maker (wants their property managed, or is evaluating the company as a vendor) or a renter (wants to occupy a unit). The verbs give it away: managing, listing, hiring, evaluating, switching, and "my property/my rental" point to an owner; renting, touring, applying, and moving in point to a renter.
- When role is genuinely unclear, default to the interpretation the caller's own words support most directly, rather than the one that requires the most assumptions.
- Treat missing specifics (no property count, no address, no budget) as normal for someone still deciding whether to engage — absence of detail is not a reason to reclassify. Lean on what direction they're moving, not how much they've told you.
- Only move away from New Business when the caller supplies a signal that actively points elsewhere (they're trying to rent a home themselves, or they already have property managed by the company). Silence on those points, combined with owner-side language, defaults to New Business.

## Selection Criteria

- Caller is asking about property management services, what the company does, or whether it covers their area.
- Caller is asking about pricing, fee structure, or the onboarding process.
- Caller is currently with another property manager and asking about switching.
- Caller is requesting a consultation, proposal, demo, or next steps toward becoming a client.
- Caller is a property owner or decision-maker evaluating the company as a vendor — not a renter, and not an already-managed owner.

## Out-of-scope

- Caller wants to rent a unit themselves -> Leasing
- Current owner with a property already managed by the company -> route to their account team, not New Business
- Current owner asking about adding another property -> still New Business if evaluating terms, otherwise account management`,
            officeHours: OfficeHoursMode.ALWAYS,
        },
        isActive: true,
    },
    {
        id: 'leasing-inbound',
        name: 'Leasing',
        description: 'Prospective renters asking about availability, tours, pricing, or applying',
        defaultSettings: {
            type: 'LEASING',
            name: 'Leasing',
            selectionCriteria: `## Context

Leasing inquiries refer to prospective tenants (renters) trying to find and secure a rental unit or property. This includes questions about unit availability, rent pricing, floor plans, amenities, pet policies, screening/application criteria, scheduling or confirming a showing/tour, and the application/approval process itself. It covers anyone from "just looking" browsers to applicants actively trying to get approved for a specific unit. Callers may not name a specific property or state their intent precisely — a vague "do you have anything available?" or "I saw your listing" is still a leasing inquiry.

### How to infer intent when the caller is vague

Don't wait for a perfectly stated request. Anchor on two things — the caller's role and the direction of the action — and reason from there:
- Identify whether the caller is acting as a renter (wants to occupy a unit) or an owner (wants their property managed). The verbs give it away: renting, touring, applying, moving in, and signing point to a renter; managing, listing, hiring, and "my property/my rental" point to an owner.
- When role is genuinely unclear, default to the interpretation the caller's own words support most directly, rather than the one that requires the most assumptions.
- Treat missing specifics (no address, no unit number, no price given) as normal for a prospective renter — absence of detail is not a reason to reclassify. Lean on what direction they're moving, not how much they've told you.
- Only move away from Leasing when the caller supplies a signal that actively points elsewhere (they already have a lease, they own the property, they're reporting a problem in a unit they occupy). Silence on those points defaults to Leasing.

## Selection Criteria

- Caller is asking about unit availability, pricing, amenities, floor plans, or pet policy.
- Caller wants to schedule, reschedule, or confirm a showing/tour.
- Caller is asking about application requirements, screening criteria, or application status (pre-approval/pre-lease).
- Caller is a prospective renter, not a property owner and not an already-leased resident.

## Out-of-scope

- Caller is an owner asking about property management services -> New Business
- Caller has already signed a lease and is asking about move-in date, keys, or walkthrough -> Move-ins
- Current resident asking to extend/renew their existing lease -> Lease Renewal (not one of these five)
- Caller reports a problem with a unit -> Maintenance
- Current resident asking about ending their lease -> Move-outs`,
            officeHours: OfficeHoursMode.ALWAYS,
        },
        isActive: true,
    },
    {
        id: 'maintenance-inbound',
        name: 'Maintenance',
        description: 'Current residents reporting a problem — routine repairs through urgent emergencies',
        defaultSettings: {
            type: 'MAINTENANCE',
            name: 'Maintenance',
            selectionCriteria: `## Context

Maintenance inquiries refer to a current tenant/resident reporting a problem with their rented unit that needs repair or attention. This spans the full range from routine issues (leaky faucet, appliance not working, pest control, minor repairs) to urgent/emergency issues posing an immediate safety or habitability threat (fire, flooding, gas smell, no heat in winter, total loss of power/water, break-in, lockout). It also includes following up on a previously submitted maintenance request. Callers may not describe the problem precisely or know whether it's "an emergency" — a vague "something's wrong with my unit" or "my AC isn't working right" is still a maintenance inquiry.

### How to infer intent when the caller is vague

Don't wait for a perfectly stated request. Anchor on two things — the caller's role and the state of the unit — and reason from there:
- Identify whether the caller is a current resident (occupies the unit) reporting something wrong right now. The verbs and nouns give it away: broken, leaking, not working, stopped, won't turn on, smell, no power/heat/water point to an active problem in an occupied unit.
- When severity is unclear, reason from consequence, not from the caller's wording — a problem that threatens safety or habitability (gas, fire, flood, no heat in winter, lockout, break-in) is the emergency end of the same scenario; a problem that's an inconvenience is the routine end. Both are Maintenance.
- Treat missing specifics (no ticket number, no exact appliance, no clear cause) as normal for someone who just wants it fixed — absence of detail is not a reason to reclassify. Lean on the fact that they occupy the unit and something is wrong.
- Only move away from Maintenance when the caller ties the condition to the start or end of a lease rather than an active tenancy problem (a walkthrough finding at move-in, or a damage/deposit item at move-out). Silence on those points defaults to Maintenance.

## Selection Criteria

- Caller is reporting something broken, malfunctioning, or unsafe in their unit.
- Caller is reporting an urgent safety or habitability threat (fire, flooding, gas smell, no heat in winter, total loss of power/water, break-in, lockout).
- Caller is following up on a maintenance request already submitted (status, ETA, tech didn't show).
- The problem is happening now, in a unit the caller currently occupies.

## Out-of-scope

- Damage or condition noted as part of the move-in walkthrough -> Move-ins
- Damage assessment or repair tied to a departing tenant / the deposit -> Move-outs
- A prospective tenant asking about a unit's condition before renting -> Leasing`,
            officeHours: OfficeHoursMode.ALWAYS,
        },
        isActive: true,
    },
    {
        id: 'move-ins-inbound',
        name: 'Move-ins',
        description: 'Newly-leased residents coordinating move-in date, keys, and onboarding',
        defaultSettings: {
            type: null,
            name: 'Move-ins',
            selectionCriteria: `## Context

Move-in inquiries refer to a tenant who has already signed a lease and is in the process of beginning their tenancy. This includes coordinating the move-in date, key/access/gate code handoff, move-in walkthrough or condition report, utility transfer instructions, parking/mailbox setup, and general "what do I need to know before/on move-in day" onboarding questions. Callers may not state that they've signed a lease — a vague "when can I get my keys?" or "what do I need for move-in day?" is still a move-in inquiry.

### How to infer intent when the caller is vague

Don't wait for a perfectly stated request. Anchor on two things — where the caller sits in the lease lifecycle and the direction of the action — and reason from there:
- Identify whether the caller has already committed to a lease and is now entering the unit. Language like keys, access, gate code, move-in date, walkthrough, "getting set up," and utilities-in-my-name points to someone who has already leased and is arriving.
- When it's unclear whether the caller has signed, lean on whether they speak as someone who has secured the unit (asking about logistics of arriving) versus someone still shopping for one (asking about availability or price). The former is Move-ins; the latter is Leasing.
- Treat missing specifics (no exact date, no unit named) as normal for someone coordinating a move — absence of detail is not a reason to reclassify. Lean on the fact that they're moving toward taking possession.
- Only move away from Move-ins when the caller supplies a signal that actively points elsewhere (they haven't leased yet, they're reporting a repair unrelated to move-in condition, or they're planning to leave). Silence on those points defaults to Move-ins.

## Selection Criteria

- Caller has a signed lease and is coordinating their move-in date or logistics.
- Caller is asking about key/access/gate code handoff.
- Caller is asking about the move-in walkthrough or condition report.
- Caller is asking about utility transfer, parking, mailbox, or general move-in day onboarding.
- The caller has secured the unit and the topic is entering it.

## Out-of-scope

- A repair issue arising after move-in, unrelated to move-in condition -> Maintenance
- Caller is planning to leave the unit -> Move-outs`,
            officeHours: OfficeHoursMode.ALWAYS,
        },
        isActive: true,
    },
    {
        id: 'move-outs-inbound',
        name: 'Move-outs',
        description: 'Departing residents giving notice, scheduling inspection, or asking about their deposit',
        defaultSettings: {
            type: null,
            name: 'Move-outs',
            selectionCriteria: `## Context

Move-out inquiries refer to a current tenant who is ending their tenancy. This includes giving notice to vacate, coordinating the move-out date, move-out inspection scheduling, cleaning/damage expectations, security deposit return timeline or disputes, key/access surrender, and forwarding address updates. Callers may not use formal language — a vague "I'm thinking about leaving" or "how do I get my deposit back?" is still a move-out inquiry.

### How to infer intent when the caller is vague

Don't wait for a perfectly stated request. Anchor on two things — where the caller sits in the lease lifecycle and the direction of the action — and reason from there:
- Identify whether the caller is a current resident whose action points toward exiting the tenancy. Language like moving out, giving notice, leaving, ending my lease, deposit back, final inspection, and forwarding address points to a departure.
- When it's unclear whether they're leaving or staying, lean on direction: talk of vacating, notice, and deposits points to Move-outs, while talk of continuing, extending, or renewing points to Lease Renewal.
- Treat missing specifics (no exact move-out date, no notice given yet) as normal for someone still planning their exit — absence of detail is not a reason to reclassify. Lean on the fact that they're moving toward ending the tenancy.
- Only move away from Move-outs when the caller supplies a signal that actively points elsewhere (they want to stay and extend, or they want a repair done for their remaining time). Silence on those points defaults to Move-outs.

## Selection Criteria

- Caller is giving notice, has given notice, or has already vacated.
- Caller is coordinating a move-out date or move-out inspection.
- Caller is asking about cleaning/damage expectations or the security deposit (timeline or dispute).
- Caller is asking about key/access surrender or updating a forwarding address.
- The caller's tenancy is ending or ended and the topic is exit or deposit.

## Out-of-scope

- Caller wants to stay and extend -> Lease Renewal (not one of these five)
- A repair request for the remaining time in the unit -> Maintenance
- An owner asking about turning over or re-leasing the vacated unit -> New Business or Leasing depending on whether they're evaluating the company or already a client`,
            officeHours: OfficeHoursMode.ALWAYS,
        },
        isActive: true,
    },
];
/** Get active scenario templates only (isActive and not comingSoon). */
export function getActiveScenarioTemplates() {
    return SCENARIO_TEMPLATES.filter(t => t.isActive && !t.comingSoon);
}
/** Get a scenario template by id. */
export function getScenarioTemplateById(id) {
    return SCENARIO_TEMPLATES.find(t => t.id === id);
}
//# sourceMappingURL=scenario-templates.js.map